import React, { useEffect, useState } from 'react';
import MasterComponent from "../../../../../../components/Forms";
import { MasterConfigs } from "../../../../../../interfaces/entity";
import { Form, Input, Select, Button, Table, Switch, InputNumber, Space, Popconfirm, FormInstance, TableColumnProps, ColorPicker } from 'antd';
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';

const { Option } = Select;

interface FieldData {
    key: number;
    label?: string;
    calculation?: string;
    dependentField?: string;
    rangeRequired?: string;
    rangeCustomization?: any[];
    formula?: string;
    formulaLabel?: string;
    componentCategory?: string;
    sequence?: number;
    background?: string;
}

const CustomSalary = () => {
    const [form, setForm] = useState<FormInstance>(null);
    const [dataSource, setDataSource] = useState<FieldData[]>([]);
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const isEditing = (record: FieldData) => record.key.toString() === editingKey;

    const EditableCell: React.FC<any> = ({
        editing,
        dataIndex,
        title,
        record,
        children,
        ...restProps
    }) => {
        let inputNode;
        const onSelect = (value: string, _dataIndex) => {
            if (_dataIndex === 'rangeRequired' && value === 'No') {
                form.setFieldsValue({ rangeCustomization: record.rangeCustomization || [] });
            }
            const newData = [...dataSource];
            const index = newData.findIndex((item) => item.key === record.key);
            if (index > -1) {
                newData[index] = { ...newData[index], [_dataIndex]: value };
                setDataSource(newData);
            }
        }

        if (dataIndex === 'calculation') {
            inputNode = (
                <Select onChange={(e) => onSelect(e, dataIndex)} allowClear>
                    <Option value="MONTH">Month</Option>
                    <Option value="Annual">Annual</Option>
                    <Option value="BOTH">Both</Option>
                    <Option value="NONE">None</Option>
                </Select>
            );
        } else if (dataIndex === 'rangeRequired') {
            inputNode = <Select onChange={(e) => onSelect(e, dataIndex)} allowClear>
                <Option value="Yes">Yes</Option>
                <Option value="No">No</Option>
            </Select>
        } else if (dataIndex === 'rangeCustomization') {
            inputNode = (
                record?.rangeRequired == 'Yes' ? <Form.List name="rangeCustomization">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }, index) => (
                                <Space
                                    key={key}
                                    style={{ display: 'flex', marginBottom: 8 }}
                                    align="baseline"
                                >
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'range1']}
                                        rules={[
                                            { required: true, message: 'Missing Range 1 value' },
                                        ]}
                                    >
                                        <Input placeholder="Range 1" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'range2']}
                                        rules={[
                                            { required: true, message: 'Missing Range 2 value' },
                                        ]}
                                    >
                                        <Input placeholder="Range 2" />
                                    </Form.Item>
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'range3']}
                                        rules={[
                                            { required: true, message: 'Missing Range 3 value' },
                                        ]}
                                    >
                                        <Input placeholder="Range 3" />
                                    </Form.Item>
                                    <Space size={[0, 0]} direction='horizontal'>
                                        {fields.length > 1 && <Button
                                            type="text"
                                            icon={<DeleteOutlined />}
                                            onClick={() => remove(name)}
                                            danger
                                        />}
                                        {fields.length == (index + 1) && <Button
                                            type="text"
                                            onClick={() => add({ range1: '', range2: '', range3: '' })}
                                            icon={<PlusCircleOutlined />}
                                        />}
                                    </Space>
                                </Space>
                            ))}

                        </>
                    )}
                </Form.List> : null);
        } else if (dataIndex === 'dependentField') {
            inputNode = <Select onChange={(e) => onSelect(e, dataIndex)} allowClear>
                {dataSource.filter(ele => ele.label).map(ele => <Option value={ele?.label}>{ele?.label}</Option>)}
            </Select>
        } else if (dataIndex === 'sequence') {
            inputNode = <InputNumber />;
        } else if (dataIndex == 'background') {
            inputNode = <ColorPicker />;
        } else {
            inputNode = <Input />;
        }

        return (
            <td {...restProps}>
                {editing ? (
                    <Form.Item
                        name={dataIndex}
                        style={{ margin: 0 }}
                    >
                        {inputNode}
                    </Form.Item>
                ) : (
                    children
                )}
            </td>
        );
    };

    const edit = (record: FieldData) => {
        form.setFieldsValue({ ...record, rangeCustomization: record.rangeCustomization || [] });
        setEditingKey(record.key.toString());
    };

    const cancel = () => {
        setEditingKey(null);
    };

    const save = async (key: number) => {
        try {
            const row = (await form.validateFields()) as FieldData;
            const newData = [...dataSource];
            const index = newData.findIndex((item) => item.key === key);

            if (index > -1) {
                newData[index] = { ...newData[index], ...row };
                setDataSource(newData);
                setEditingKey(null);
            }
        } catch (errInfo) {
            console.error('Validate Failed:', errInfo);
        }
    };

    const addRow = () => {
        const newRow: FieldData = {
            key: Date.now(),
            label: '',
            calculation: '',
            dependentField: '',
            rangeRequired: '',
            rangeCustomization: [{ range1: '', range2: '', range3: '' }],
            formula: '',
            formulaLabel: '',
            componentCategory: '',
            sequence: dataSource.length,
            background: ''
        };
        setDataSource([...dataSource, newRow]);
    };

    const removeRow = (key: number) => {
        setDataSource((prev) => prev.filter((item) => item.key !== key));
    };

    const columns: TableColumnProps[] | any[] = [
        {
            title: 'Label',
            dataIndex: 'label',
            editable: true,
            width: 200,
        },
        {
            title: 'Calculation',
            dataIndex: 'calculation',
            editable: true,
            width: 200,
        },
        {
            title: 'Dependent Field',
            dataIndex: 'dependentField',
            width: 200,
            editable: true,
        },
        {
            title: 'Range Required',
            dataIndex: 'rangeRequired',
            width: 200,
            editable: true,
        },
        {
            title: 'Range Customization',
            dataIndex: 'rangeCustomization',
            width: 400,
            editable: true,
            render: (customization: any) => {
                if (Array.isArray(customization)) {
                    return customization.map((item, index) => (
                        <div key={index}>
                            <Space direction='horizontal'>
                                {Object.entries(item).map(([key, value]: any) => value).join(' - ')}
                            </Space>
                        </div>
                    ));
                }
                return null; // If not an array, render nothing or fallback content
            },
        },
        {
            title: 'Formula',
            dataIndex: 'formula',
            width: 200,
            editable: true,
        },
        {
            title: 'Formula Label',
            dataIndex: 'formulaLabel',
            width: 200,
            editable: true,
        },
        {
            title: 'Category',
            dataIndex: 'componentCategory',
            width: 200,
            editable: true,
        },
        {
            title: 'Sequence',
            dataIndex: 'sequence',
            width: 200,
            editable: true
        },
        {
            title: 'Background ',
            dataIndex: 'background',
            width: 150,
            editable: true
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            width: 200,
            render: (_: any, record: FieldData) =>
                isEditing(record) ? (
                    <>
                        <a onClick={() => save(record.key!)} style={{ marginRight: 8 }}>
                            Save
                        </a>
                        <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                            <a>Cancel</a>
                        </Popconfirm>
                    </>
                ) : (
                    <>
                        <a onClick={() => edit(record)} style={{ marginRight: 8 }}>
                            Edit
                        </a>
                        <Popconfirm title="Are you sure to delete this row?" onConfirm={() => removeRow(record.key!)}>
                            <a>Delete</a>
                        </Popconfirm>
                    </>
                ),
        },
    ];

    const mergedColumns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record: FieldData) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    const config: MasterConfigs = {
        api: () => '/custom-salary-structure-type',
        title: 'Custom Salary Structure Type',
        modalTitle: () => config.title,
        drawerProps: { width: '90%' },
        getColumns: () => [
            {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
            },
            {
                title: 'Code',
                dataIndex: 'code',
                key: 'code',
            },
            {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                width: '50%',
                ellipsis: true
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
            }
        ],
        getFormColumns: () => [
            {
                label: 'Name',
                name: 'name',
                type: 'text',
                rules: [{ required: true, message: 'Please enter name' }]
            },
            {
                label: 'Code',
                name: 'code',
                type: 'text',
                rules: [{ required: true, message: 'Please enter Code' }]
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 16
            },
            {
                type: 'custom',
                span: 24,
                formItem: (e) => <Table
                    components={{
                        body: {
                            cell: EditableCell,
                        },
                    }}
                    scroll={{ x: 1500 }}
                    bordered
                    dataSource={dataSource}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    pagination={false}
                />
            },
            {
                type: 'custom',
                span: 24,
                formItem: () => (
                    <Button
                        type="primary"
                        onClick={addRow}
                        style={{
                            marginBottom: 16,
                        }}
                    >
                        Add a row
                    </Button>
                )
            },
        ],
        onSubmit: (values) => {
            console.log('Form values:', values, dataSource);
            return Promise.reject();
        },
        onClose: () => {
            setDataSource([]);
        },
        actions: [{ actionType: 'Add', name: 'Add' }]
    }

    return <MasterComponent config={config} form={(form) => setForm(form)} />
}

export default CustomSalary;